// packages/report-components/src/SmartText.tsx
//
// A smart text block: the author's prose with live variables (plan
// 2026-10-16-a-smart-text-block, S6; ADR-0426).
//
// The SERVER resolves every variable to a display string
// (`services/composition/smart_text.py::resolve_variables`); this component
// only substitutes. `smartTextParagraphs` parses the template into the
// text-block runs first and replaces each `{{name}}` inside a run with the
// resolved string as its own run, so a value can never become markup — and
// everything here is a React node, never an HTML string.
//
// A renderer always draws `text`; the status only explains it (a tooltip).
// A token with no resolved value (the hook has not answered, or failed) is a
// "—" with status `pending`: the prose is still the author's, and a dash says
// "no number here" where an empty gap would say nothing.

import * as React from "react";
import { Text } from "./Text";
import {
  smartTextParagraphs,
  type ResolvedSmartTextVariable,
  type SmartTextRun,
  type SmartTextVariableRun,
} from "./textBlock";

/** What the block hook answers for a smart text block. Every level optional:
 *  it arrives as JSON, and a missing level is "not resolved", never a crash. */
export type SmartTextAnswer = {
  smart_text?: {
    variables?: Record<string, ResolvedSmartTextVariable> | null;
    period?: unknown;
  } | null;
} | null;

export interface SmartTextProps {
  /** The stored template: markdown with `{{name}}` tokens. */
  content?: string | null;
  /** The block hook's answer; `null`/absent draws every value as pending. */
  block?: SmartTextAnswer;
}

/** The hover sentence per status. English: UI text, never the prose. */
const STATUS_TITLE: Record<SmartTextVariableRun["status"], string | undefined> = {
  ok: undefined,
  no_value: "No value for this period",
  no_comparison: "No data to compare with",
  partial_comparison: "Compared over the days this period has data for",
  comparison_failed: "The comparison could not be loaded",
  pending: "Not loaded yet",
};

function resolvedOf(block: SmartTextAnswer | undefined): Readonly<Record<string, ResolvedSmartTextVariable>> {
  const variables = block?.smart_text?.variables;
  return variables && typeof variables === "object" ? variables : {};
}

function VariableRun({ run }: { run: SmartTextVariableRun }): React.ReactElement {
  const value = (
    <span
      data-smart-text-variable={run.variable}
      data-status={run.status}
      title={STATUS_TITLE[run.status]}
    >
      {run.text}
    </span>
  );
  if (run.style === "strong") return <strong>{value}</strong>;
  if (run.style === "em") return <em>{value}</em>;
  if (run.style === "del") return <s>{value}</s>;
  return value;
}

function RunNode({ run }: { run: SmartTextRun }): React.ReactElement {
  if ("variable" in run) return <VariableRun run={run} />;
  if ("strong" in run) return <strong>{run.strong}</strong>;
  if ("em" in run) return <em>{run.em}</em>;
  if ("del" in run) return <s>{run.del}</s>;
  // A link draws its label only in a report (a user-controlled href in a
  // delivered document is a phishing surface; textBlock.ts).
  return <>{run.text}</>;
}

export function SmartText({ content, block }: SmartTextProps): React.ReactElement {
  const paragraphs = smartTextParagraphs(content, resolvedOf(block));
  return (
    <div data-smart-text="">
      {paragraphs.map((runs, i) => (
        <Text key={i}>
          {runs.length === 0 ? "" : runs.map((run, j) => <RunNode key={j} run={run} />)}
        </Text>
      ))}
    </div>
  );
}

export interface SmartTextLineProps {
  /** One paragraph: a block subtitle's template, plain or with `{{name}}`. */
  content?: string | null;
  /** The resolved variables in the block hook's answer shape; absent for a
   *  plain subtitle, and every token reads "—" (pending) until it answers. */
  block?: SmartTextAnswer;
  className?: string;
}

/**
 * The same substitution as `SmartText`, inline: one `<span>` of runs, no
 * paragraph element. A block subtitle (plan 2026-10-16-a-block-subtitle) is
 * one paragraph under a frame title, drawn here on the report and on the
 * dashboard tile alike, so a plain subtitle and one with variables go
 * through the same parse (`*x*` is italic in both) and a value can never
 * become markup.
 */
export function SmartTextLine({ content, block, className }: SmartTextLineProps): React.ReactElement | null {
  if (!content) return null;
  const runs = smartTextParagraphs(content, resolvedOf(block)).flat();
  return (
    <span data-smart-text-line="" className={className}>
      {runs.map((run, i) => <RunNode key={i} run={run} />)}
    </span>
  );
}

